import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  Building2, 
  Plus, 
  Search, 
  Filter,
  MoreVertical,
  ExternalLink,
  Users,
  Calendar
} from 'lucide-react'
import api from '../lib/api'
import { formatDate, getStatusColor } from '../lib/utils'

export default function BusinessesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)

  const { data: businesses, isLoading } = useQuery({
    queryKey: ['businesses', { search: searchTerm, status: statusFilter, page: currentPage }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      })
      if (searchTerm) params.append('search', searchTerm)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      
      const response = await api.get(`/admin/tenants?${params.toString()}`)
      // Backend returns { success, data: { businesses: [...], pagination: {...} } }
      return response.data.data
    }
  })

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value)
    setCurrentPage(1) // Reset to first page when filtering
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Businesses</h1>
          <p className="text-muted-foreground">Manage all customer organizations</p>
        </div>
        <Link to="/businesses/new" className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Business
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search businesses..."
            value={searchTerm}
            onChange={handleSearch}
            className="input pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={handleStatusFilter}
          className="input w-40"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Businesses Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Subdomain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/3"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/3"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/4"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/4"></div>
                    </td>
                  </tr>
                ))
              ) : businesses?.businesses?.length > 0 ? (
                businesses.businesses.map((business) => (
                  <tr key={business.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">{business.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {business.billing_email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">
                          {business.subdomain}.easica.com
                        </span>
                        <a
                          href={`http://${business.subdomain}.easica.com`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(business.status)}`}>
                        {business.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {business.plan_display_name || business.plan || 'Free'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <Users className="h-4 w-4 mr-1 text-muted-foreground" />
                        {business.user_count || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(business.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/businesses/${business.id}`}
                          className="text-primary hover:text-primary/80"
                        >
                          View
                        </Link>
                        <button className="text-muted-foreground hover:text-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">
                    No businesses found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {businesses?.pagination && businesses.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((businesses.pagination.page - 1) * pageSize) + 1} to{' '}
            {Math.min(businesses.pagination.page * pageSize, businesses.pagination.total)} of{' '}
            {businesses.pagination.total} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!businesses.pagination.hasPrev}
              className="btn btn-outline btn-sm"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {businesses.pagination.page} of {businesses.pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!businesses.pagination.hasNext}
              className="btn btn-outline btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
